import * as io from "io-ts";
import createGlobalState from "@ui/lib/createGlobalState";
import t from "../../../../../i18n";
import { notNull } from "diagnostics-lib/typescript";
import React from "react";
import { uniqBy } from "diagnostics-lib/array";
import { comparatorBy } from "diagnostics-lib/comparator";
import { useTranslations } from "../../../../../api/useTranslations";
import type { DiagnosisReporting } from "../../../../../api/useReporting";

export type FailureAggregation =
  | "FAILURE"
  | "CATEGORY"
  | "REPARABILITY"
  | "DEGRADATION_TYPE"
  | "SYSTEM";
export type FailureAggregationOptions = { label: string; value: FailureAggregation }[];

export const failureAggregationOptions: FailureAggregationOptions = [
  {
    label: t("reporting.chart.failure_aggregation.failure"),
    value: "FAILURE",
  },
  {
    label: t("reporting.chart.failure_aggregation.category"),
    value: "CATEGORY",
  },
  {
    label: t("reporting.chart.failure_aggregation.reparability"),
    value: "REPARABILITY",
  },
  {
    label: t("reporting.chart.failure_aggregation.degradationType"),
    value: "DEGRADATION_TYPE",
  },
  {
    label: t("reporting.chart.failure_aggregation.system"),
    value: "SYSTEM",
  },
];

export const useFailureAggegation = createGlobalState({
  storage: "session",
  key: "reportingFailureAggegation",
  type: io.union([
    io.literal("FAILURE"),
    io.literal("CATEGORY"),
    io.literal("REPARABILITY"),
    io.literal("DEGRADATION_TYPE"),
    io.literal("SYSTEM"),
  ]),
  defaultValue: "CATEGORY",
  scoped: false,
});

export const REST_PROJECT_AGGREGATION_TARGET_ID = "rest";

export function useFailureAggregationTargets(reporting: DiagnosisReporting) {
  const translations = useTranslations();
  const [selectedProjectAggregation] = useFailureAggegation({});

  return React.useMemo(() => {
    if (selectedProjectAggregation === "REPARABILITY") {
      const reparabilitiesFromPeriod = reporting.periods
        .flatMap(p =>
          p.values.flatMap(v => v.impacts.flatMap(({ reparability }) => reparability || [])),
        )
        .filter((r, i, arr) => r !== null && arr.indexOf(r) === i)
        .flatMap(r => ({ id: r, name: t("reparability", r, translations) }));

      const reparabilities = [...reparabilitiesFromPeriod]; // ...reparabilitiesFromFailures

      const uniqueReparabilities = uniqBy(reparabilities, "id").sort(comparatorBy("id"));

      return [
        ...uniqueReparabilities,
        {
          id: REST_PROJECT_AGGREGATION_TARGET_ID,
          name: t("reporting.chart.aggregation.failure.other", translations),
        },
      ];
    }

    let mostRecentFailureDisplayNameByFailureTechnicalName: Record<string, string> = {};
    if (selectedProjectAggregation === "FAILURE") {
      mostRecentFailureDisplayNameByFailureTechnicalName = [...reporting.periods]
        .sort((a, b) => b.period - a.period)
        .flatMap(p => p.values.flatMap(v => v.impacts))
        .reduce(
          (acc, { failureId }) => {
            const failure = reporting.failureById[failureId];
            if (!failure) return acc;
            if (!acc[failure.name] && failure.displayName) {
              acc[failure.name] = failure.displayName;
            }
            return acc;
          },
          {} as Record<string, string>,
        );
    }

    const otherAggregation = (reporting ? Object.values(reporting.failureById) : [])
      .map(({ id, name, failureCategoryId, degradationTypeId, systemId }) => {
        if (
          selectedProjectAggregation === "FAILURE" &&
          id &&
          mostRecentFailureDisplayNameByFailureTechnicalName[name]
        )
          return {
            id: name,
            name: mostRecentFailureDisplayNameByFailureTechnicalName[name],
          };
        /**
         * From 3.1 version, application categories are unique by their name (id === human readable name).
         */
        if (selectedProjectAggregation === "CATEGORY" && failureCategoryId)
          return { id: failureCategoryId, name: failureCategoryId };
        if (selectedProjectAggregation === "DEGRADATION_TYPE" && degradationTypeId)
          return { id: degradationTypeId, name: degradationTypeId };
        if (selectedProjectAggregation === "SYSTEM" && systemId)
          return { id: systemId, name: systemId };
        return null;
      })
      .filter(notNull)
      .filter((target, i, arr) => arr.findIndex(t => t.id === target.id) === i)
      .sort((a, b) => a.name.localeCompare(b.name));

    return [
      ...otherAggregation,
      {
        id: REST_PROJECT_AGGREGATION_TARGET_ID,
        name: t("reporting.chart.aggregation.failure.other", translations),
      },
    ];
  }, [reporting, selectedProjectAggregation, translations]);
}
